import React from 'react';
import { MessageSquare, Palette, Code2, TestTube, Rocket } from 'lucide-react';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';

const steps = [
  {
    title: 'Strategy Session',
    description: 'We validate your idea and define the MVP features',
    icon: MessageSquare,
    outcome: 'Clear roadmap eliminates guesswork and sets realistic expectations'
  },
  {
    title: 'UX/UI Design',
    description: 'Design every screen and interaction for maximum user engagement',
    icon: Palette,
    outcome: 'Interactive prototypes let you test user flows before development'
  },
  {
    title: 'Development Sprint',
    description: 'Build using proven frameworks and best practices',
    icon: Code2,
    outcome: 'Weekly demos keep you informed and allow for quick adjustments'
  },
  {
    title: 'Testing & Launch',
    description: 'Real user testing and App Store deployment',
    icon: TestTube,
    outcome: 'Thorough testing ensures a smooth launch with minimal bugs'
  },
  {
    title: 'Growth & Support',
    description: 'Ongoing optimization and feature development',
    icon: Rocket,
    outcome: 'Continuous improvement drives user acquisition and retention'
  }
];

export const AppDevelopmentProcess = () => {
  const { elementRef, getRevealStyle } = useScrollReveal();

  return (
    <section className="build-rhythm-section">
      <div className="container-professional">
        <div className="section-header-professional">
          <div className="micro-label-professional">BUILD RHYTHM</div>
          <h2 className="heading-section">Clear communication and predictable delivery</h2>
          <p className="text-body max-w-2xl mx-auto">
            Our proven process ensures transparency and measurable progress at every step.
          </p>
        </div>

        <div ref={elementRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 max-w-7xl mx-auto">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <div 
                key={index} 
                className="relative group"
                style={getRevealStyle(index)}
              >
                <div 
                  className="bg-[#111111] border border-[rgba(255,255,255,0.15)] rounded-xl p-6 h-full flex flex-col transition-all duration-200 hover:transform hover:-translate-y-1 hover:border-[rgba(255,255,255,0.25)]"
                >
                  {/* Step Number */}
                  <div className="text-xs font-mono font-semibold tracking-wider mb-4" style={{ color: 'rgba(255, 255, 255, 0.4)' }}>
                    STEP {index + 1}
                  </div>
                  
                  {/* Icon */}
                  <Icon 
                    className="w-8 h-8 mb-4" 
                    strokeWidth={1.5}
                    style={{ color: '#00e5ff' }}
                  />
                  
                  {/* Title */}
                  <h3 className="text-lg font-semibold text-white mb-3 leading-tight">
                    {step.title}
                  </h3>
                  
                  {/* Description */}
                  <p className="text-sm leading-relaxed mb-4 flex-grow" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    {step.description}
                  </p>
                  
                  {/* Outcome */}
                  <div className="pt-4 mt-auto border-t border-[rgba(255,255,255,0.08)]">
                    <p className="text-xs leading-relaxed" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                      {step.outcome}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};